/**
 * Attribution / UTM capture.
 *
 * On every fresh landing:
 *   1. Read utm_source/utm_medium/utm_campaign/utm_content/utm_term from URL.
 *   2. Store them in sessionStorage so they survive across pages until signup.
 *   3. Fire a POST /api/attribution/pageview so we have a server-side click count.
 *
 * On signup forms:
 *   - Read the stored attribution via `getStoredAttribution()` and include it
 *     in the signup request. The backend stamps it onto the brand/user row.
 *
 * Design notes:
 *   - "First-touch" wins: we only overwrite the stored attribution if the new
 *     landing has fresh UTM params. Direct visits after a UTM-tagged one keep
 *     the original source.
 *   - Pageviews are best-effort; failures never surface to the user.
 */

const STORAGE_KEY = 'freddy_attribution_v1';
const SESSION_KEY = 'freddy_session_id_v1';

function getSessionId() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function readUtmsFromLocation() {
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    const out = {};
    let any = false;
    for (const k of keys) {
      const v = params.get(k);
      if (v) { out[k] = v.slice(0, 200); any = true; }
    }
    return any ? out : null;
  } catch { return null; }
}

/**
 * Persist first-touch attribution.
 * @returns {object|null} the current stored attribution
 */
function persistAttributionIfPresent() {
  const fresh = readUtmsFromLocation();
  if (fresh) {
    const stored = readStored();
    // First touch wins — only overwrite if we had nothing.
    if (!stored || !stored.utm_source) {
      const payload = {
        ...fresh,
        referrer: document.referrer || '',
        landing_path: window.location.pathname,
        captured_at: new Date().toISOString(),
      };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch {}
      return payload;
    }
    return stored;
  }
  return readStored();
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function getStoredAttribution() {
  return readStored() || {};
}

/**
 * Fire once per session per path — non-blocking.
 * We use sessionStorage to dedupe within a browser tab lifetime.
 */
function firePageview(apiBase) {
  const path = window.location.pathname;
  const sessionId = getSessionId();
  const dedupeKey = `pv_${sessionId}_${path}`;
  if (sessionStorage.getItem(dedupeKey) === '1') return;
  sessionStorage.setItem(dedupeKey, '1');
  const utms = readUtmsFromLocation() || {};
  const body = {
    path,
    referrer: document.referrer || '',
    session_id: sessionId,
    landing: !document.referrer || !document.referrer.includes(window.location.host),
    utm_source: utms.utm_source || null,
    utm_medium: utms.utm_medium || null,
    utm_campaign: utms.utm_campaign || null,
    utm_content: utms.utm_content || null,
    utm_term: utms.utm_term || null,
  };
  try {
    // sendBeacon survives page navigation
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon(`${apiBase}/attribution/pageview`, blob);
      return;
    }
    fetch(`${apiBase}/attribution/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch { /* stat never breaks UX */ }
}

/**
 * Call once from App on every navigation.
 */
export function trackPageview(apiBase) {
  persistAttributionIfPresent();
  firePageview(apiBase);
}
